import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ArbeitszeitDto } from '../../models/arbeitszeit/arbeitszeit-dto.model';
import { DokumentBezeichnungDto } from '../../models/base/dokument-bezeichnung-dto.model';
import { UrpBasRequestDto } from '../../models/urp/urp-bas-req-dto.model';
import { UrpCodeDto } from '../../models/urp/urp-code-dto.model';
import { UrpCodeRequestDto } from '../../models/urp/urp-code-req-dto.model';
import { UrpInternAbsenzenDto } from '../../models/urp/urp-intern-absenzen-dto.model';
import { UrpProjektRequestDto } from '../../models/urp/urp-projekt-req-dto.model';
import { UrpTagAuswertungDto } from '../../models/urp/urp-tag-auswertung-dto.model';
import { UrpTagDto } from '../../models/urp/urp-tag-dto.model';
import { UrpTagRequestDto } from '../../models/urp/urp-tag-req-dto.model';
import { ApiService } from '../../services/api.service';

@Injectable({
  providedIn: 'root',
})
export class UrpTagService {
  private _http = inject(HttpClient);
  private _apiService = inject(ApiService);

  public findDay(datum: string): Promise<UrpTagDto> {
    return firstValueFrom(this._http.get<UrpTagDto>(`${this._apiService.getApiUrl()}/urp/tage/find?datum=${datum}`));
  }

  public findTagId(datum: string, prId: number, basId: number): Promise<number> {
    return firstValueFrom(
      this._http.get<number>(
        `${this._apiService.getApiUrl()}/urp/tage/find/id?datum=${datum}&prId=${prId}&basId=${basId}`,
      ),
    );
  }

  public updateDayDetails(
    tagId: number,
    wetterCode: number,
    temperatur: string,
    beschrieb: string,
    detail: string,
  ): Promise<any> {
    const urpTagRequestDto = new UrpTagRequestDto();
    urpTagRequestDto.wetterCode = wetterCode;
    urpTagRequestDto.temperatur = temperatur;
    urpTagRequestDto.beschrieb = beschrieb;
    urpTagRequestDto.detail = detail;

    return firstValueFrom(this._http.put(`${this._apiService.getApiUrl()}/urp/tage/${tagId}`, urpTagRequestDto));
  }

  public deleteDay(tagId: number): Promise<any> {
    return firstValueFrom(this._http.delete<any>(`${this._apiService.getApiUrl()}/urp/tage/${tagId}`));
  }

  public addProject(tagId: number, prId: number, basId: number, addKbs: boolean): Promise<any> {
    const urpProjektRequestDto = new UrpProjektRequestDto();
    urpProjektRequestDto.prId = prId;
    urpProjektRequestDto.basId = basId;
    urpProjektRequestDto.addKbs = addKbs;

    return firstValueFrom(
      this._http.post(`${this._apiService.getApiUrl()}/urp/tage/${tagId}/projekte`, urpProjektRequestDto),
    );
  }

  public deleteProject(tagId: number, prId: number): Promise<any> {
    return firstValueFrom(this._http.delete<any>(`${this._apiService.getApiUrl()}/urp/tage/${tagId}/projekte/${prId}`));
  }

  public getBasList(tagId: number, prId: number): Promise<DokumentBezeichnungDto[]> {
    return firstValueFrom(
      this._http.get<DokumentBezeichnungDto[]>(
        `${this._apiService.getApiUrl()}/urp/tage/${tagId}/projekte/${prId}/bas`,
      ),
    ).then();
  }

  public addBas(tagId: number, prId: number, basId: number, addKbs: boolean) {
    const urpBasRequestDto = new UrpBasRequestDto();
    urpBasRequestDto.basId = basId;
    urpBasRequestDto.addKbs = addKbs;

    return firstValueFrom(
      this._http.post(`${this._apiService.getApiUrl()}/urp/tage/${tagId}/projekte/${prId}/bas`, urpBasRequestDto),
    );
  }

  public deleteBas(tagId: number, prId: number, basId: number): Promise<any> {
    return firstValueFrom(
      this._http.delete<any>(`${this._apiService.getApiUrl()}/urp/tage/${tagId}/projekte/${prId}/bas/${basId}`),
    );
  }

  public cleanupBas() {
    return firstValueFrom(this._http.get(`${this._apiService.getApiUrl()}/urp/tage/bas/cleanup`));
  }

  public getCodes(tagId: number): Promise<UrpCodeDto[]> {
    return firstValueFrom(this._http.get<UrpCodeDto[]>(`${this._apiService.getApiUrl()}/urp/tage/${tagId}/codes`));
  }

  public addCode(tagId: number, codeId: number): Promise<any> {
    const urpCodeRequestDto = new UrpCodeRequestDto();
    urpCodeRequestDto.codeId = codeId;

    return firstValueFrom(
      this._http.post(`${this._apiService.getApiUrl()}/urp/tage/${tagId}/codes`, urpCodeRequestDto),
    );
  }

  public deleteCode(tagId: number, codeId: number): Promise<any> {
    return firstValueFrom(this._http.delete<any>(`${this._apiService.getApiUrl()}/urp/tage/${tagId}/codes/${codeId}`));
  }

  // Obsolete ab Bauplus-Version 10.38.5.0
  public saveArbeitszeit(tagId: number, arbeitszeitDto: ArbeitszeitDto): Promise<any> {
    return firstValueFrom(
      this._http.post(`${this._apiService.getApiUrl()}/urp/tage/${tagId}/arbeitszeit`, arbeitszeitDto),
    );
  }

  // Obsolete ab Bauplus-Version 10.38.5.0
  public deleteArbeitszeit(tagId: number): Promise<any> {
    return firstValueFrom(this._http.delete(`${this._apiService.getApiUrl()}/urp/tage/${tagId}/arbeitszeit`));
  }

  public getInternAbsenzen(tagId: number, date: string, urpTeam: string): Promise<UrpInternAbsenzenDto[]> {
    return firstValueFrom(
      this._http.get<UrpInternAbsenzenDto[]>(
        `${this._apiService.getApiUrl()}/urp/tage/${tagId}/teamInternAbsenzen?date=${date}&urpTeam=${urpTeam}`,
      ),
    );
  }

  public getTagAuswertung(tagId: number, date: string): Promise<UrpTagAuswertungDto[]> {
    return firstValueFrom(
      this._http.get<UrpTagAuswertungDto[]>(
        `${this._apiService.getApiUrl()}/urp/tage/${tagId}/auswertungTag?date=${date}`,
      ),
    );
  }
}
